import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useCountdown } from "@/components/AuctionCard";
import { Label } from "@/components/auction/AuctionUI";
import { Trophy } from "lucide-react";
import { HitButton, useAsyncAction, useHeavyFlow } from "@/components/loading";

function Box({ label, children, sub, tone }) {
  const coral = tone === "coral";
  return (
    <div className={`flex-1 rounded-[16px] p-3.5 border ${coral ? "border-[#FF4438]/45 bg-[#FF4438]/[.08]" : "border-white/12 bg-black/30"}`}>
      <Label>{label}</Label>
      {children}
      <div className="text-xs text-white/55 mt-1">{sub}</div>
    </div>
  );
}

function Opt({ on, onClick, children, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
            className={`flex-1 h-[52px] rounded-[14px] border text-[15px] transition-colors ${on ? "border-[#FF4438]/80 bg-[#FF4438]/[.12] font-extrabold text-white" : "border-white/14 bg-black/20 text-white/75 hover:border-white/30"}`}>
      {children}
    </button>
  );
}

export function WinnerPanel({ a, onDone }) {
  const navigate = useNavigate();
  const o = a.my_offer;
  const [method, setMethod] = useState("transfer");
  const [delivery, setDelivery] = useState("ship");
  const [address, setAddress] = useState("");
  const left = useCountdown(o?.deadline);
  const acceptAct = useAsyncAction(async () => {
    try { await api.post(`/auctions/${a.id}/accept-offer`); return () => { toast.success("Aceptaste. Ahora completa el pago."); onDone(); }; }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo aceptar"); return false; }
  });
  const declineAct = useAsyncAction(async () => {
    if (!window.confirm("¿Rechazar esta oferta? Pasará al siguiente postor y no podrás recuperarla. No tiene penalidad.")) return false;
    try { await api.post(`/auctions/${a.id}/decline-offer`); return () => { toast.success("Rechazaste la oferta. No tiene penalidad."); onDone(); }; }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo rechazar"); return false; }
  });
  const payAct = useAsyncAction(async () => {
    try {
      setFlow({ step: 1, progress: null });
      const r = await api.post(`/auctions/${a.id}/checkout`, { payment_method: method, delivery, shipping_address: address });
      setFlow({ step: 3, progress: null });
      if (r.data.checkout_url) { window.location.href = r.data.checkout_url; await new Promise(() => {}); }
      return () => navigate(`/order/${r.data.order_id}`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "No se pudo crear el pago");
      return false;
    }
  });
  const setFlow = useHeavyFlow(payAct.loading, "payment");
  const busy = acceptAct.loading || payAct.loading || declineAct.loading;
  if (!o || !["awaiting_payment", "repechage_offered"].includes(a.result)) return null;
  const won = o.rank === 1;
  const activeOrder = o.order_id && !["CANCELADO", "RECHAZADO", "PENDIENTE_PAGO"].includes(o.order_status);
  const pay = () => {
    if (delivery === "ship" && !address.trim()) { toast.error("Ingresa la dirección de envío"); return; }
    payAct.run();
  };
  const accent = won ? "#34D399" : "#fbbf24";
  return (
    <div data-testid="winner-panel" className="relative overflow-hidden rounded-[28px] p-5 space-y-4 border"
         style={{ borderColor: won ? "rgba(52,211,153,.45)" : "rgba(245,158,11,.45)", background: won ? "linear-gradient(180deg,#0c1a15,#0b0f0d)" : "linear-gradient(180deg,#1a140a,#100d08)" }}>
      <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full pointer-events-none" style={{ background: `radial-gradient(circle, ${won ? "rgba(52,211,153,.18)" : "rgba(245,158,11,.16)"}, transparent 66%)` }} />
      <div className="relative">
        <div className="sx-mono text-[10px] tracking-[.14em] flex items-center gap-2" style={{ color: accent }}><Trophy size={13} /> {won ? "SUBASTA GANADA" : "REPECHAJE"}</div>
        <h2 className="sx-h text-[32px] mt-2">{won ? <>¡Ganaste <span className="sx-it">esta subasta!</span></> : <>Te ofrecemos <span className="sx-it">esta subasta</span></>}</h2>
        {!won && <p className="text-sm text-white/65 mt-1.5">El ganador no pagó y la subasta pasó a ti por ser el siguiente postor. Puedes aceptarla o rechazarla.</p>}
      </div>
      <div className="relative flex gap-2.5">
        <Box label="Pagas tu puja" sub={delivery === "meetup" ? "sin costo de envío" : "+ envío"}><div className="sx-price text-[28px] text-[#34D399] mt-1">${o.amount}</div></Box>
        <Box label="Plazo" sub={!won && !o.accepted ? "para decidir y pagar" : "para pagar"} tone="coral"><div className="sx-mono text-[22px] text-[#FF4438] mt-1" data-testid="offer-countdown">{left}</div></Box>
      </div>
      {!won && !o.accepted ? (
        <div className="relative text-sm text-white/75" data-testid="offer-decline-note">No estás obligado a comprarla: si la rechazas o dejas pasar el plazo sin aceptar, <b className="text-white">no hay penalidad</b>. Solo si la aceptas debes pagar dentro del plazo o tu cuenta será <b className="text-[#ff6b61]">BANEADA</b>.</div>
      ) : (
        <div className="relative text-sm font-bold text-[#ff6b61]">Si no pagas dentro del plazo, tu cuenta será BANEADA.</div>
      )}
      {activeOrder ? (
        <Link to={`/order/${o.order_id}`} data-testid="winner-order-link" className="sx-btn relative h-[56px] flex items-center justify-center text-base">Ir a mi pedido</Link>
      ) : !o.accepted ? (
        <HitButton onClick={acceptAct.run} disabled={busy} loading={acceptAct.loading} done={acceptAct.done} loadingText="Confirmando…" doneText="Confirmada" tone="dark" data-testid="offer-accept-btn" className="sx-btn relative w-full h-[56px] text-base inline-flex items-center justify-center gap-2.5">Aceptar y continuar al pago</HitButton>
      ) : null}
      {!activeOrder && !o.accepted && (
        <HitButton onClick={declineAct.run} disabled={busy} loading={declineAct.loading} done={declineAct.done} loadingText="Rechazando…" doneText="Rechazada" tone="dark" data-testid="offer-decline-btn" className="sx-btn-ghost relative w-full h-12 text-sm inline-flex items-center justify-center gap-2.5">No me interesa, rechazar</HitButton>
      )}
      {!activeOrder && o.accepted && (
        <div className="relative space-y-3">
          <div className="sx-label">1 · ¿Cómo la recibes?</div>
          <div className="flex gap-2">
            <Opt on={delivery === "ship"} onClick={() => setDelivery("ship")} testid="winner-delivery-ship">Envío</Opt>
            {a.accepts_local_delivery && <Opt on={delivery === "meetup"} onClick={() => setDelivery("meetup")} testid="winner-delivery-meetup">Entrega personal</Opt>}
          </div>
          {delivery === "ship" && <input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Dirección de envío completa" data-testid="winner-address" className="sx-input w-full h-[52px] px-4 text-[15px]" />}
          {delivery === "meetup" && a.meetup_location && (
            <div className="rounded-[14px] border border-white/14 bg-black/25 px-4 py-3 text-sm text-white/80" data-testid="winner-meetup-point">
              Punto de encuentro: <b className="text-white">{a.meetup_location}</b> · {a.city}
            </div>
          )}
          <div className="sx-label pt-1">2 · ¿Cómo pagas?</div>
          <div className="flex gap-2">
            <Opt on={method === "transfer"} onClick={() => setMethod("transfer")} testid="winner-method-transfer">Transferencia</Opt>
            <Opt on={method === "payphone"} onClick={() => setMethod("payphone")} testid="winner-method-card">Tarjeta</Opt>
          </div>
          <HitButton onClick={pay} disabled={busy} loading={payAct.loading} done={payAct.done} loadingText="Procesando pago…" doneText="Listo" tone="dark" data-testid="winner-pay-btn" className="sx-btn w-full h-[56px] text-base inline-flex items-center justify-center gap-2.5">
            Pagar ahora
          </HitButton>
        </div>
      )}
    </div>
  );
}

export default WinnerPanel;
